import React, { useState, useEffect } from 'react';
import apiClient from '../../utils/apiClient';
import ConfirmationModal from './ConfirmationModal';
import './FirmwareUpdateModal.css';

const FirmwareUpdateModal = ({ onClose }) => {
  const [deviceKind, setDeviceKind] = useState('');
  const [version, setVersion] = useState('');
  const [chipVersion, setChipVersion] = useState('');
  const [fileBase64, setFileBase64] = useState('');
  const [error, setError] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmIsError, setConfirmIsError] = useState(false);
  const [firmwares, setFirmwares] = useState([]);
  const [latestByChipset, setLatestByChipset] = useState({});

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = (reader.result || '').split(',')[1];
      setFileBase64(base64);
    };
  };

  useEffect(() => {
    const fetchFw = async () => {
      try {
        const { data } = await apiClient.get('/firmware_versions');
        setFirmwares(data || []);
      } catch {}
    };
    fetchFw();
  }, []);

  useEffect(() => {
    if (!deviceKind) {
      setLatestByChipset({});
      return;
    }
    const filtered = firmwares.filter(f => f.DeviceKind === deviceKind);
    const result = {};
    ['00', '01'].forEach(cs => {
      const latest = filtered
        .filter(f => f.ChipsetVersion === cs)
        .sort((a, b) => {
          const va = a.FirmwareVersion.split('.').slice(1).join('.');
          const vb = b.FirmwareVersion.split('.').slice(1).join('.');
          return va < vb ? 1 : -1;
        })[0];
      if (latest) result[cs] = latest.FirmwareVersion;
    });
    setLatestByChipset(result);
  }, [deviceKind, firmwares]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (!fileBase64 || !deviceKind.trim() || !version.trim()) {
      setError('All fields are required.');
      return;
    }
    try {
      await apiClient.post('/firmware_versions', {
        FirmwareFile: fileBase64,
        FirmwareVersion: version,
        ChipsetVersion: chipVersion,
        DeviceKind: deviceKind
      });
      setConfirmMessage('Firmware update request sent!');
      setConfirmIsError(false);
      setShowConfirmModal(true);
    } catch (err) {
      console.error(err);
      setError('Failed to push firmware update. Please try again.');
      setConfirmMessage('Failed to push firmware update. Please try again.');
      setConfirmIsError(true);
      setShowConfirmModal(true);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Push Firmware Update</h2>
        {error && <div className="modal-error">{error}</div>}
        <form onSubmit={handleSubmit} className="firmware-form">
          <label>
            Device Type
            <select
              value={deviceKind}
              onChange={e => setDeviceKind(e.target.value)}
              required
            >
              <option value="" disabled>Select a device</option>
              <option value="lock">iLockBlue</option>
              <option value="beam">iBeam</option>
              <option value="latch">Latch</option>
            </select>
          </label>

          <label>
            Chipset Number
            <input
              type="text"
              value={chipVersion}
              onChange={e => setChipVersion(e.target.value)}
              placeholder="e.g. 00"
              required
            />
          </label>

          <label>
            Version Number
            <input
              type="text"
              value={version}
              onChange={e => setVersion(e.target.value)}
              placeholder="e.g. 12.34"
              required
            />
          </label>
         
          {deviceKind && (
            <div className="latest-firmwares">
              <p>Latest Firmwares</p>
              {latestByChipset['00'] && <p>00.{latestByChipset['00']}</p>}
              {latestByChipset['01'] && <p>01.{latestByChipset['01']}</p>}
            </div>
          )}

          <label>
            Firmware File
            <input
              type="file"
              accept=".gbl"
              onChange={handleFileChange}
              required
            />
          </label>

          <div className="modal-buttons">
            <button
              className="firmware-modal-button cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="firmware-modal-button confirm" type="submit">
              Submit
            </button>
          </div>
        </form>
      </div>

      {showConfirmModal && (
        <ConfirmationModal
          isOpen={showConfirmModal}
          message={confirmMessage}
          confirmLabel="OK"
          showCancel={false}
          isDanger={confirmIsError}
          onConfirm={() => {
            setShowConfirmModal(false);
            if (!confirmIsError) onClose();
          }}
        />
      )}
    </div>
  );
};

export default FirmwareUpdateModal;